import {
  Coins,
  SquaresFour,
  ChartLine,
  PiggyBank,
  Receipt,
  ArrowClockwise,
  Gear,
  Target,
} from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { paths } from '../../routes/paths';

/** Entrada de navegação (link). */
export const NAV_ITEM_DASHBOARD = {
  to: paths.dashboard,
  label: pt.nav.dashboard,
  icon: SquaresFour,
  end: true,
};

/**
 * Rail tablet (md–lg): atalhos planos alinhados ao mockup Responsivo.
 * Menu abre o drawer com o resto das áreas.
 */
export const TABLET_RAIL_ITEMS = [
  NAV_ITEM_DASHBOARD,
  {
    to: paths.expenses,
    label: pt.nav.expenses,
    icon: Receipt,
  },
  {
    to: paths.patrimonio,
    label: pt.nav.patrimonio,
    icon: ChartLine,
  },
  {
    to: paths.goals,
    label: pt.nav.goals,
    icon: Target,
    matchGoalsTree: true,
  },
];

/** Cinco áreas por intenção + filhos em substantivo (mockup Menu). */
export const NAV_AREAS = [
  {
    id: 'my-month',
    label: pt.nav.sections.myMonth,
    icon: ArrowClockwise,
    items: [
      {
        to: paths.selfies,
        label: pt.nav.selfies,
        matchSelfieTree: true,
      },
      {
        to: paths.cycleSettings,
        label: pt.nav.cycleSettings,
      },
    ],
  },
  {
    id: 'expenses',
    label: pt.nav.expenses,
    icon: Receipt,
    items: [
      {
        to: paths.expenses,
        label: pt.nav.expenses,
      },
      {
        to: paths.calendar,
        label: pt.nav.calendarAnnual,
      },
      {
        to: paths.budget,
        label: pt.nav.budget,
      },
    ],
  },
  {
    id: 'income',
    label: pt.nav.income,
    icon: Coins,
    items: [
      {
        to: paths.income,
        label: pt.nav.incomeHistory,
        matchIncomeTree: true,
      },
      {
        to: paths.incomeSources,
        label: pt.nav.incomeSourcesShort,
      },
    ],
  },
  {
    id: 'savings',
    label: pt.nav.sections.savings,
    icon: PiggyBank,
    items: [
      {
        to: paths.goals,
        label: pt.nav.goals,
        matchGoalsTree: true,
      },
      {
        to: paths.pots,
        label: pt.nav.pots,
      },
      {
        to: paths.emergencyFund,
        label: pt.nav.emergencyFund,
      },
    ],
  },
  {
    id: 'patrimonio',
    label: pt.nav.patrimonio,
    icon: ChartLine,
    items: [
      {
        to: paths.patrimonio,
        label: pt.nav.patrimonioOverview,
      },
      {
        to: paths.ativos,
        label: pt.nav.assets,
      },
      {
        to: paths.dividas,
        label: pt.nav.dividas,
      },
    ],
  },
  {
    id: 'settings',
    label: pt.nav.settings,
    icon: Gear,
    /** Área de topo: o cabeçalho é o próprio link (sem filhos). */
    to: paths.settings,
    items: [],
  },
];

/** Itens só no rodapé (logout fica no layout; Definições vive em NAV_AREAS). */
export const FOOTER_NAV_ITEMS = [];

/** @deprecated Use NAV_AREAS — lista plana derivada das áreas. */
export const MAIN_NAV_ITEMS = NAV_AREAS.flatMap((area) => {
  if (area.to && (!area.items || area.items.length === 0)) {
    return [{ to: area.to, label: area.label, icon: area.icon }];
  }
  return area.items;
});

/** @deprecated Mantido para compatibilidade. */
export const SIDEBAR_NAV_SECTIONS = NAV_AREAS;

/** Lista plana de todos os itens (útil para detecção de rota activa). */
export const ALL_NAV_ITEMS = [
  NAV_ITEM_DASHBOARD,
  ...MAIN_NAV_ITEMS,
  ...FOOTER_NAV_ITEMS,
];

/** @deprecated Use ALL_NAV_ITEMS. */
export const SIDEBAR_NAV_ITEMS = ALL_NAV_ITEMS;

/** Atalhos fixos na barra inferior mobile (< md): Início | Despesas | FAB | Património | Menu */
export const BOTTOM_NAV_ITEMS = [
  NAV_ITEM_DASHBOARD,
  {
    to: paths.expenses,
    label: pt.nav.expenseList,
    shortLabel: pt.nav.expenses,
    icon: Receipt,
  },
  {
    to: paths.patrimonio,
    label: pt.nav.patrimonioOverview,
    shortLabel: pt.nav.patrimonio,
    icon: ChartLine,
  },
];

/** Secções completas para a sidebar desktop. */
export function getDesktopNavSections() {
  return NAV_AREAS;
}

/** Menu mobile — omite itens marcados como bottomBarOnly. */
export function getMobileMenuSections() {
  return NAV_AREAS.map((area) => ({
    ...area,
    items: (area.items || []).filter((item) => !item.bottomBarOnly),
  })).filter((area) => (area.items && area.items.length > 0) || area.to);
}

/** @deprecated Use getMobileMenuSections().flatMap… */
export const MOBILE_MENU_NAV_ITEMS = getMobileMenuSections().flatMap((s) =>
  s.to && (!s.items || s.items.length === 0)
    ? [{ to: s.to, label: s.label, icon: s.icon }]
    : s.items,
);

export function isNavItemActive(pathname, { to, end, matchIncomeTree, matchGoalsTree, matchSelfieTree }) {
  if (matchSelfieTree) {
    if (pathname === paths.selfies) return true;
    return pathname.startsWith(`${paths.app}/selfie`);
  }
  if (matchGoalsTree) {
    return pathname === paths.goals || pathname.startsWith(`${paths.goals}/`);
  }
  if (matchIncomeTree) {
    if (pathname === to) return true;
    if (!pathname.startsWith(`${paths.income}/`)) return false;
    return !pathname.startsWith(`${paths.incomeSources}`);
  }
  if (end) {
    return pathname === to;
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function sectionHasActiveItem(pathname, section) {
  if (section.to && isNavItemActive(pathname, { to: section.to })) {
    return true;
  }
  return (section.items || []).some((item) => isNavItemActive(pathname, item));
}
